// Soft solfeggio healing-tone generator using Web Audio API.
// Each oil has its own tonal signature tied to its spiritual purpose.
// All tones are synthesized in-browser (no audio files, no network).

let sharedContext = null;

const getContext = () => {
  if (typeof window === 'undefined') return null;
  if (sharedContext && sharedContext.state !== 'closed') return sharedContext;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  sharedContext = new Ctx();
  return sharedContext;
};

// Solfeggio frequency reference (Hz)
// 396 - liberation from fear, guilt
// 417 - facilitating change, undoing situations
// 432 - natural tuning, calm grounding
// 528 - love, healing, transformation
// 639 - harmonious relationships, heart chakra
// 741 - awakening intuition, releasing
// 852 - spiritual awakening
// 963 - divine / crown chakra

// Product ID -> frequency signature (fundamental + shimmer)
const PRODUCT_TONES = {
  1: [528, 639], // Beauty Within — love + harmony
  2: [528, 639], // Loving On Me — love + heart-opening
  3: [639, 963], // Goddess Essence — harmony + divine/crown
  4: [432, 528], // Winding Down — grounding calm + soft love
  5: [432, 396], // Inner Peace — calm + fear-release
  6: [396, 741], // Release Me — liberation + awakening
  7: [528, 639], // Divine Goddess Scent — love + harmony (Aphrodite essence)
};

// Short human-readable label for each product's tonal signature
const PRODUCT_TONE_LABELS = {
  1: { hz: '528 Hz', meaning: 'love' },
  2: { hz: '528 Hz', meaning: 'love' },
  3: { hz: '639 Hz', meaning: 'harmony' },
  4: { hz: '432 Hz', meaning: 'calm' },
  5: { hz: '432 Hz', meaning: 'peace' },
  6: { hz: '396 Hz', meaning: 'release' },
  7: { hz: '528 Hz', meaning: 'love' },
};

/**
 * Returns the display label for a product's tonal signature.
 * @param {number|string} productId
 * @returns {{hz: string, meaning: string} | null}
 */
export const getProductToneLabel = (productId) =>
  productId != null ? PRODUCT_TONE_LABELS[productId] || null : null;

// Default (unknown product) — gentle universal chime
const DEFAULT_TONES = [528, 963];

/**
 * Plays a soft bell-like healing chime tuned to the product.
 * @param {object} options
 * @param {number|string} [options.productId] - Product ID; selects tone signature.
 * @param {number} [options.volume=0.08] - Master volume (0-1). Keep low.
 * @param {number[]} [options.frequencies] - Override tone set (skips productId lookup).
 * @param {number} [options.duration=2.2] - Total tone duration in seconds.
 */
export const playHealingChime = ({
  productId,
  volume = 0.08,
  frequencies,
  duration = 2.2,
} = {}) => {
  const ctx = getContext();
  if (!ctx) return;

  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  const tones =
    frequencies ||
    (productId != null && PRODUCT_TONES[productId]) ||
    DEFAULT_TONES;

  const now = ctx.currentTime;

  // Master gain: soft attack, long exponential release
  const master = ctx.createGain();
  master.gain.setValueAtTime(0.0001, now);
  master.gain.exponentialRampToValueAtTime(volume, now + 0.08);
  master.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  master.connect(ctx.destination);

  // Warm low-pass so the tone feels gentle, not sharp
  const lowpass = ctx.createBiquadFilter();
  lowpass.type = 'lowpass';
  lowpass.frequency.value = 4000;
  lowpass.Q.value = 0.7;
  lowpass.connect(master);

  tones.forEach((freq, idx) => {
    const osc = ctx.createOscillator();
    osc.type = 'sine';
    osc.frequency.value = freq;

    // Fundamental louder, shimmer softer
    const oscGain = ctx.createGain();
    oscGain.gain.value = idx === 0 ? 1.0 : 0.45;

    osc.connect(oscGain);
    oscGain.connect(lowpass);

    osc.start(now);
    osc.stop(now + duration + 0.05);
  });
};

export default playHealingChime;

/**
 * Plays a 1-minute layered healing meditation for a product. Composed of:
 *   - A breathing sustained pad (slow 0.12 Hz tremolo, slightly detuned chorus)
 *   - Sub-octave warmth
 *   - Tibetan singing bowls (inharmonic partials, slow attack, long decay)
 *   - Meditation bells (quick attack, 3-partial sines, medium decay)
 *   - Wind chime cascades (3-4 quick bells at ascending pentatonic intervals)
 * All instruments are scheduled on a timeline so a new sound appears every few seconds.
 *
 * @param {object} options
 * @param {number|string} [options.productId] - Product ID; selects tone signature.
 * @param {number} [options.duration=60] - Total duration in seconds.
 * @param {number} [options.volume=0.2] - Master volume (0-1).
 * @param {() => void} [options.onEnd] - Called when playback finishes naturally.
 * @returns {{stop: () => void}} - Handle to stop playback manually.
 */
export const playHealingMusic = ({
  productId,
  duration = 60,
  volume = 0.2,
  onEnd,
} = {}) => {
  const ctx = getContext();
  if (!ctx) return { stop: () => {} };

  if (ctx.state === 'suspended') {
    ctx.resume().catch(() => {});
  }

  const tones =
    (productId != null && PRODUCT_TONES[productId]) || DEFAULT_TONES;
  const [fundamental, shimmer] = tones;

  const now = ctx.currentTime;
  const oscillators = [];

  // --- MASTER CHAIN: instruments → warmLowpass → masterGain → destination ---
  const masterGain = ctx.createGain();
  masterGain.gain.setValueAtTime(0.0001, now);
  masterGain.gain.exponentialRampToValueAtTime(volume, now + 3);
  masterGain.gain.setValueAtTime(volume, now + Math.max(duration - 3, 3));
  masterGain.gain.exponentialRampToValueAtTime(0.0001, now + duration);
  masterGain.connect(ctx.destination);

  const warmLowpass = ctx.createBiquadFilter();
  warmLowpass.type = 'lowpass';
  warmLowpass.frequency.value = 6500; // lets bell brightness through while staying warm
  warmLowpass.Q.value = 0.6;
  warmLowpass.connect(masterGain);

  // --- BREATHING PAD: chorus + slow tremolo ---
  const padGain = ctx.createGain();
  padGain.gain.value = 0.28;
  padGain.connect(warmLowpass);

  // LFO at 0.12 Hz modulates pad gain (breathing quality)
  const padLfo = ctx.createOscillator();
  padLfo.type = 'sine';
  padLfo.frequency.value = 0.12;
  const padLfoGain = ctx.createGain();
  padLfoGain.gain.value = 0.12;
  padLfo.connect(padLfoGain);
  padLfoGain.connect(padGain.gain);
  padLfo.start(now);
  padLfo.stop(now + duration + 0.1);
  oscillators.push(padLfo);

  // Two slightly detuned voices for chorus shimmer
  [-6, 6].forEach((cents) => {
    const pad = ctx.createOscillator();
    pad.type = 'sine';
    pad.frequency.value = fundamental;
    pad.detune.value = cents;
    pad.connect(padGain);
    pad.start(now);
    pad.stop(now + duration + 0.1);
    oscillators.push(pad);
  });

  // Sub-octave pad for warmth
  const sub = ctx.createOscillator();
  sub.type = 'sine';
  sub.frequency.value = fundamental / 2;
  const subGain = ctx.createGain();
  subGain.gain.value = 0.16;
  sub.connect(subGain);
  subGain.connect(warmLowpass);
  sub.start(now);
  sub.stop(now + duration + 0.1);
  oscillators.push(sub);

  // --- INSTRUMENT HELPERS ---

  /** Tibetan singing bowl — 4 inharmonic partials, slow attack, long decay. */
  const scheduleBowl = (offset, baseFreq, gainScale = 1.0) => {
    const startAt = now + offset;
    const decay = 7.0;
    [
      { ratio: 1.0, level: 0.5 },
      { ratio: 2.08, level: 0.26 },
      { ratio: 2.76, level: 0.14 },
      { ratio: 5.4, level: 0.07 },
    ].forEach(({ ratio, level }) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = baseFreq * ratio;
      osc.detune.value = (Math.random() - 0.5) * 4;
      const oscGain = ctx.createGain();
      oscGain.gain.setValueAtTime(0.0001, startAt);
      oscGain.gain.exponentialRampToValueAtTime(level * gainScale, startAt + 0.3);
      oscGain.gain.exponentialRampToValueAtTime(0.0001, startAt + decay);
      osc.connect(oscGain);
      oscGain.connect(warmLowpass);
      osc.start(startAt);
      osc.stop(startAt + decay + 0.1);
      oscillators.push(osc);
    });
  };

  /** Meditation bell — quick attack, 3 harmonic partials, medium decay. */
  const scheduleBell = (offset, baseFreq, gainScale = 1.0) => {
    const startAt = now + offset;
    const decay = 3.0;
    [
      { ratio: 1.0, level: 0.45 },
      { ratio: 2.0, level: 0.22 },
      { ratio: 3.0, level: 0.10 },
    ].forEach(({ ratio, level }) => {
      const osc = ctx.createOscillator();
      osc.type = 'sine';
      osc.frequency.value = baseFreq * ratio;
      const oscGain = ctx.createGain();
      oscGain.gain.setValueAtTime(0.0001, startAt);
      oscGain.gain.exponentialRampToValueAtTime(level * gainScale, startAt + 0.02);
      oscGain.gain.exponentialRampToValueAtTime(0.0001, startAt + decay);
      osc.connect(oscGain);
      oscGain.connect(warmLowpass);
      osc.start(startAt);
      osc.stop(startAt + decay + 0.1);
      oscillators.push(osc);
    });
  };

  /** Wind chime cascade — 4 quick bells at ascending pentatonic-like intervals. */
  const scheduleWindChime = (offset, baseFreq, gainScale = 1.0) => {
    const delays = [0, 0.16, 0.32, 0.48];
    const ratios = [1.0, 1.25, 1.5, 2.0];
    delays.forEach((d, i) => {
      scheduleBell(offset + d, baseFreq * ratios[i] * 1.5, 0.45 * gainScale);
    });
  };

  // --- TIMELINE: compose 60 seconds of rich variation ---
  //   0s  Opening deep bowl (fundamental)
  //   6s  Light meditation bell (shimmer)
  //  13s  Shimmer singing bowl
  //  20s  Wind chime cascade (shimmer-based)
  //  25s  High bright bell (2× fundamental)
  //  30s  Mid-point bowl (fundamental)
  //  37s  Harmony bell (shimmer)
  //  43s  Wind chime cascade (high fundamental)
  //  48s  Final shimmer bowl (decays into fade-out)
  //  55s  Gentle closing bell
  scheduleBowl(0, fundamental, 1.0);
  scheduleBell(6, shimmer, 0.9);
  scheduleBowl(13, shimmer, 0.85);
  scheduleWindChime(20, shimmer, 1.0);
  scheduleBell(25, fundamental * 2, 0.8);
  scheduleBowl(30, fundamental, 0.9);
  scheduleBell(37, shimmer, 0.9);
  scheduleWindChime(43, fundamental * 2, 0.85);
  scheduleBowl(48, shimmer, 0.8);
  scheduleBell(55, fundamental, 0.55);

  // --- END HANDLING ---
  let finished = false;
  const endTimer = setTimeout(() => {
    if (finished) return;
    finished = true;
    if (onEnd) onEnd();
  }, Math.floor(duration * 1000));

  const stop = () => {
    if (finished) return;
    finished = true;
    clearTimeout(endTimer);
    try {
      const currentValue = masterGain.gain.value;
      const stopAt = ctx.currentTime + 0.6;
      masterGain.gain.cancelScheduledValues(ctx.currentTime);
      masterGain.gain.setValueAtTime(currentValue, ctx.currentTime);
      masterGain.gain.exponentialRampToValueAtTime(0.0001, stopAt);
      oscillators.forEach((o) => {
        try {
          o.stop(stopAt + 0.05);
        } catch (_) {
          /* noop */
        }
      });
    } catch (_) {
      /* noop */
    }
    if (onEnd) onEnd();
  };

  return { stop };
};
